import axios from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { useUserStore } from '@/stores/user'
import { getSystemHeader } from '@/utils/systemHeader'
// import { ElMessage } from 'element-plus'

// 防重复处理标志（参考可灵AI等主流应用的处理方式）
let isHandlingAuthExpired = false
let authExpiredTimer: ReturnType<typeof setTimeout> | null = null
let refreshPromise: Promise<string> | null = null

const V1_UNAUTHORIZED_CODE = 4001

type AuthRequestConfig = InternalAxiosRequestConfig & {
  /** 401 时不跳登录页（公开接口可匿名访问） */
  noAuthRedirect?: boolean
  /** 401 时不走 refreshToken（登录 / 发短信 / 刷新令牌本身） */
  skipAuthRefresh?: boolean
  __isRetryRequest?: boolean
}

/**
 * 获取 API Base URL
 *
 * - 开发环境：固定用 `/api`，走 Vite 代理
 * - 生产环境（打包后）：
 *   - 优先用 `VITE_API_BASE_URL`（如果配置了）
 *   - 否则用当前访问域名 + `/api`，打到哪就请求哪
 */
const getBaseURL = (): string => {
  const { DEV, VITE_API_BASE_URL } = import.meta.env

  if (DEV) {
    // dev 统一走本地代理，简单稳定
    return '/api'
  }

  // 如果需要固定 API 域名（多前端共用一个后端），直接在 .env 里配 VITE_API_BASE_URL
  if (VITE_API_BASE_URL) {
    return VITE_API_BASE_URL as string
  }

  // 默认：自动跟随当前部署域名
  if (typeof window !== 'undefined') {
    const { origin } = window.location
    return `${origin}/api`
  }

  // 极端情况（SSR 等没有 window）：退回相对路径
  return '/api'
}

// 创建 axios 实例
const api: AxiosInstance = axios.create({
  baseURL: getBaseURL(),
  timeout: 60000, // 60 秒请求超时
})

// 请求拦截器
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 可以在这里添加token等认证信息
    const token = localStorage.getItem('token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // 为所有请求设置 Content-Type（包括 GET 请求）
    if (config.headers && !config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json;charset=UTF-8'
    }

    // 每个请求必带 system 请求头（JSON 字符串，含 appCode / 设备信息）
    if (config.headers) {
      config.headers.system = getSystemHeader()
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 处理登录过期的统一函数（参考 uniapp 的处理方式）
const handleAuthExpired = (redirectToLogin = true) => {
  // 如果正在处理中，直接返回，避免重复提示
  if (isHandlingAuthExpired) {
    return
  }

  // 设置处理标志，防止重复处理
  isHandlingAuthExpired = true

  console.log(
    redirectToLogin
      ? '[Request] 检测到未登录或token过期，跳转登录页'
      : '[Request] 检测到未登录或token过期，已清理登录态'
  )

  // 清除本地存储的token和用户信息
  localStorage.removeItem('token')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('userInfo')

  // 清理 Pinia Store 状态
  try {
    const userStore = useUserStore()
    userStore.clearUserInfo()
  } catch (error) {
    console.error('[Request] 清理 Store 状态失败:', error)
  }

  // 清除之前的定时器（如果有）
  if (authExpiredTimer) {
    clearTimeout(authExpiredTimer)
  }

  // 公开接口（noAuthRedirect）只清登录态，不强制跳登录页
  if (redirectToLogin && typeof window !== 'undefined') {
    window.location.href = '/login'
  }

  // 重置标志
  isHandlingAuthExpired = false
  authExpiredTimer = null
}

const isAuthExemptRequest = (config?: AuthRequestConfig) => {
  const url = `${config?.baseURL || ''}${config?.url || ''}`
  return (
    url.includes('/v1/users/refresh-token') ||
    url.includes('/v1/users/mobile-login') ||
    url.includes('/v1/sms/verification-codes')
  )
}

const isUnauthorizedResponse = (error: any) => {
  const status = error?.response?.status
  const data = error?.response?.data
  return status === 401 || (data && typeof data === 'object' && data.code === V1_UNAUTHORIZED_CODE)
}

const persistRefreshedTokens = (accessToken: string, refreshToken?: string) => {
  localStorage.setItem('token', accessToken)
  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken)
  }

  try {
    const userStore = useUserStore()
    userStore.applyTokens(accessToken, refreshToken)
  } catch (error) {
    console.error('[Request] 更新刷新令牌失败:', error)
  }
}

const refreshAccessToken = async () => {
  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem('refreshToken')
    if (!refreshToken) {
      throw new Error('missing refresh token')
    }

    const res: any = await api.post(
      '/v1/users/refresh-token',
      { refreshToken },
      { noAuthRedirect: true, skipAuthRefresh: true } as any
    )

    if (res?.code !== 0 || !res?.data?.accessToken) {
      throw new Error(res?.message || 'refresh token failed')
    }

    persistRefreshedTokens(res.data.accessToken, res.data.refreshToken)
    return res.data.accessToken as string
  })()

  try {
    return await refreshPromise
  } finally {
    refreshPromise = null
  }
}

const refreshAndRetry = async (config: AuthRequestConfig) => {
  const accessToken = await refreshAccessToken()
  config.__isRetryRequest = true
  if (config.headers) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return api.request(config)
}

const rejectAuthExpired = (error: any) => {
  const authError: any = error || new Error('Not Authenticated')
  authError.handled = true
  authError.__AUTH_EXPIRED__ = true
  return Promise.reject(authError)
}

/** 401 / 4001：优先 refreshToken，noAuthRedirect 只控制是否跳登录页 */
const handleUnauthorizedRequest = (error: any, config?: AuthRequestConfig) => {
  if (!config || config.skipAuthRefresh || isAuthExemptRequest(config)) {
    return Promise.reject(error)
  }

  if (!config.__isRetryRequest && localStorage.getItem('refreshToken')) {
    return refreshAndRetry(config).catch((refreshError) => {
      handleAuthExpired(!config.noAuthRedirect)
      return rejectAuthExpired(refreshError)
    })
  }

  handleAuthExpired(!config.noAuthRedirect)
  return rejectAuthExpired(error)
}

// 响应拦截器
api.interceptors.response.use(
  (response: AxiosResponse) => {
    const data = response.data

    // 请求成功（HTTP 200-299），根据后端返回的数据结构处理登录过期（参考 uniapp 的逻辑）
    if (data && typeof data === 'object' && 'resp_code' in data) {
      // 检查登录过期的多种情况：
      // 1. resp_code === -1
      // 2. resp_code === 1 && resp_msg === 'Not Authenticated'
      // 3. resp_msg 包含 'invalid_token'
      const tokenExpired =
        data.resp_code === -1 ||
        (data.resp_code === 1 && data.resp_msg === 'Not Authenticated') ||
        (typeof data.resp_msg === 'string' && data.resp_msg.indexOf('invalid_token') !== -1)

      if (tokenExpired) {
        handleAuthExpired()
        // 返回一个特殊的rejected Promise，标记为已处理，避免后续错误处理
        const authError: any = new Error('Not Authenticated')
        authError.handled = true // 标记为已处理
        authError.__AUTH_EXPIRED__ = true
        return Promise.reject(authError)
      }
    }

    return data
  },
  (error) => {
    const config = error.config as AuthRequestConfig | undefined

    if (isUnauthorizedResponse(error)) {
      return handleUnauthorizedRequest(error, config)
    }

    // 检查响应数据中的业务错误码（即使 HTTP 状态码不是 401，也可能返回登录过期）
    if (error.response && error.response.data) {
      const data = error.response.data
      if (data && typeof data === 'object' && 'resp_code' in data) {
        // 检查登录过期的多种情况（参考 uniapp 的逻辑）
        const tokenExpired =
          data.resp_code === -1 ||
          (data.resp_code === 1 && data.resp_msg === 'Not Authenticated') ||
          (typeof data.resp_msg === 'string' && data.resp_msg.indexOf('invalid_token') !== -1)

        if (tokenExpired) {
          return handleUnauthorizedRequest(error, config)
        }
      }
    }

    // 处理网络错误或其他错误
    return Promise.reject(error)
  }
)

// 导出axios实例供各个API模块使用
export default api
